/**
 * Ères musicales par catégorie Horizons.
 * Chaque ère est liée à une plage de décennies et un style musical.
 * Utilisé pour l'auto-suggestion et le filtrage dans la carte Horizons.
 */

/**
 * Mapping catégorie → liste d'ères musicales.
 * Chaque ère : { id, label, tagLabel, startYear, endYear }
 *   - id : identifiant technique unique
 *   - label : libellé affiché dans l'interface
 *   - tagLabel : tag stocké (préfixé "Ere" dans les autoTags)
 *   - startYear / endYear : bornes de la plage (incluses)
 */
export const MUSICAL_ERAS = {
  pop: [
    { id: 'rock-n-roll',        label: "Rock'n'Roll / British Invasion", tagLabel: 'RockNRoll',      startYear: 1950, endYear: 1969 },
    { id: 'disco-funk',         label: 'Disco / Funk / Prog Rock',      tagLabel: 'DiscoFunk',      startYear: 1970, endYear: 1979 },
    { id: 'synthpop-new-wave',  label: 'Synthpop / New Wave',           tagLabel: 'SynthpopNewWave', startYear: 1980, endYear: 1989 },
    { id: 'grunge-britpop',     label: 'Grunge / Britpop / R&B',       tagLabel: 'GrungeBritpop',   startYear: 1990, endYear: 1999 },
    { id: 'indie-rnb',          label: 'Pop-Punk / Indie / R&B moderne',tagLabel: 'IndieRnB',       startYear: 2000, endYear: 2009 },
    { id: 'edm-kpop',           label: 'EDM / K-Pop / Trap',           tagLabel: 'EdmKpop',         startYear: 2010, endYear: 2019 },
    { id: 'hyperpop-lofi',      label: 'Hyperpop / Lo-fi',             tagLabel: 'HyperpopLofi',    startYear: 2020, endYear: 2039 }
  ],
  variety: [
    { id: 'chanson-classique',  label: 'Chanson française classique',   tagLabel: 'ChansonClassique',startYear: 1950, endYear: 1969 },
    { id: 'yeye-pop-fr',       label: 'Yéyé / Pop française',          tagLabel: 'YeyePopFr',       startYear: 1970, endYear: 1979 },
    { id: 'variete-synth',     label: 'Variété synthétique',           tagLabel: 'VarieteSynth',    startYear: 1980, endYear: 1989 },
    { id: 'pop-fr-moderne',    label: 'Pop française moderne',         tagLabel: 'PopFrModerne',    startYear: 1990, endYear: 2009 },
    { id: 'variete-urbaine',   label: 'Variété urbaine / Nouvelle scène', tagLabel: 'VarieteUrbaine', startYear: 2010, endYear: 2039 }
  ],
  film: [
    { id: 'age-or-hollywood',  label: "Âge d'or Hollywood",            tagLabel: 'AgeOrHollywood',  startYear: 1930, endYear: 1969 },
    { id: 'blockbusters',      label: 'Ère des blockbusters',          tagLabel: 'Blockbusters',    startYear: 1970, endYear: 1989 },
    { id: 'epopees-modernes',  label: 'Épopées modernes',              tagLabel: 'EpopeesModernes', startYear: 1990, endYear: 2009 },
    { id: 'cinema-contemp',    label: 'Cinéma contemporain',           tagLabel: 'CinemaContemp',   startYear: 2010, endYear: 2039 }
  ],
  game: [
    { id: 'chiptune',          label: 'Chiptune / 8-bit',              tagLabel: 'Chiptune',        startYear: 1975, endYear: 1994 },
    { id: 'jrpg-classique',    label: 'JRPG / RPG classique',          tagLabel: 'JrpgClassique',   startYear: 1995, endYear: 2004 },
    { id: 'orchestral-gaming', label: 'Orchestral gaming',             tagLabel: 'OrchestralGaming',startYear: 2005, endYear: 2014 },
    { id: 'indie-games',       label: 'Indie games',                   tagLabel: 'IndieGames',      startYear: 2015, endYear: 2039 }
  ],
  anime: [
    { id: 'anime-classique',   label: 'Anime classique',               tagLabel: 'AnimeClassique',  startYear: 1970, endYear: 1999 },
    { id: 'anime-moderne',     label: 'Anime moderne',                 tagLabel: 'AnimeModerne',    startYear: 2000, endYear: 2014 },
    { id: 'anime-contemp',     label: 'Anime contemporain',            tagLabel: 'AnimeContemp',    startYear: 2015, endYear: 2039 }
  ]
}

/**
 * Préfixe ajouté aux tagLabel pour former le tag complet.
 */
const ERA_TAG_PREFIX = 'Ere'

/**
 * Retourne l'ère musicale correspondant à une catégorie et une année.
 * @param {string} category - 'pop' | 'film' | 'game' | 'anime' | 'variety'
 * @param {number|string|null} year
 * @returns {{ id: string, label: string, tagLabel: string } | null}
 */
export function getMusicalEra(category, year) {
  if (!category || year == null) return null
  const y = Number(year)
  if (isNaN(y)) return null
  const eras = MUSICAL_ERAS[category]
  if (!eras) return null
  return eras.find(e => y >= e.startYear && y <= e.endYear) || null
}

/**
 * Retourne la liste des ères disponibles pour une catégorie.
 * @param {string} category
 * @returns {Array<{ id: string, label: string, tagLabel: string }>}
 */
export function getMusicalErasForCategory(category) {
  if (!category) return []
  return MUSICAL_ERAS[category] || []
}

/**
 * Retourne le tag formaté pour une ère (ex: 'EreChiptune', 'EreSynthpopNewWave').
 * @param {string} eraId - l'id de l'ère
 * @param {string} [category] - catégorie pour retrouver le tagLabel (optionnel si on passe directement un tagLabel)
 * @returns {string|null}
 */
export function getEraTag(eraId, category) {
  if (!eraId) return null
  // Chercher dans toutes les catégories (ou une seule si fournie)
  const categories = category ? [category] : Object.keys(MUSICAL_ERAS)
  for (const cat of categories) {
    const eras = MUSICAL_ERAS[cat]
    if (!eras) continue
    const era = eras.find(e => e.id === eraId)
    if (era) return `${ERA_TAG_PREFIX}${era.tagLabel}`
  }
  return null
}

/**
 * Retourne le tag d'ère directement depuis un tagLabel brut.
 * @param {string} tagLabel - ex: 'Chiptune', 'SynthpopNewWave'
 * @returns {string} - ex: 'EreChiptune', 'EreSynthpopNewWave'
 */
export function formatEraTag(tagLabel) {
  if (!tagLabel) return null
  return `${ERA_TAG_PREFIX}${tagLabel}`
}

/**
 * Tous les tags d'ères musicales (pour knownTags).
 * @returns {string[]}
 */
export function getAllEraTagLabels() {
  const tags = []
  for (const cat of Object.keys(MUSICAL_ERAS)) {
    for (const era of MUSICAL_ERAS[cat]) {
      tags.push(`${ERA_TAG_PREFIX}${era.tagLabel}`)
    }
  }
  return [...new Set(tags)]
}

/**
 * Retrouve l'ère à partir d'un tag (ex: 'EreChiptune' → { id: 'chiptune', ... }).
 * @param {string} tag
 * @returns {{ id: string, label: string, tagLabel: string, category: string } | null}
 */
export function findEraByTag(tag) {
  if (!tag || !tag.startsWith(ERA_TAG_PREFIX)) return null
  const tagLabel = tag.slice(ERA_TAG_PREFIX.length)
  for (const [cat, eras] of Object.entries(MUSICAL_ERAS)) {
    const era = eras.find(e => e.tagLabel === tagLabel)
    if (era) return { ...era, category: cat }
  }
  return null
}
